import { useEffect, useState } from 'react';

import { isBrowser } from '../utils/web';

/**
 * Hook to detect if the application is running in a Capacitor native environment.
 * Uses useEffect to ensure consistent rendering on both client and server.
 *
 * Key features:
 * - Detects Capacitor environment
 * - Server-side safe (returns false on server)
 * - Helps adapt UI/UX for native app context
 *
 * @returns boolean - True if running in Capacitor native environment
 *
 * @example
 * ```tsx
 * const isNative = useIsNativeApp();
 *
 * return (
 *   <div className={isNative ? 'native-layout' : 'web-layout'}>
 *     {content}
 *   </div>
 * );
 * ```
 */
export const useIsNativeApp = () => {
  const [isNative, setIsNative] = useState(false);

  useEffect(() => {
    if (!isBrowser) return;

    // Check if we're running in native Capacitor environment
    const isNativePlatform = !!window.Capacitor?.isNativePlatform();
    setIsNative(isNativePlatform);
  }, []);

  return isNative;
};
